import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router';

import { ApiStatusChips } from './ApiStatusChips';
import { ThemeToggle } from './ThemeToggle';
import { UserChip } from './UserChip';

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
}

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconOverview() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.8" {...ICON_STROKE} />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.8" {...ICON_STROKE} />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.8" {...ICON_STROKE} />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1.8" {...ICON_STROKE} />
    </svg>
  );
}

function IconPulse() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M3 12h4l2.5-6 4 12 2.5-6h5" {...ICON_STROKE} />
    </svg>
  );
}

function IconPeople() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="9" cy="8.5" r="3.2" {...ICON_STROKE} />
      <path d="M3.5 19c.8-3 3-4.5 5.5-4.5s4.7 1.5 5.5 4.5" {...ICON_STROKE} />
      <path d="M16 5.6a3.2 3.2 0 0 1 0 5.8M17.5 14.9c1.6.6 2.7 2 3 4.1" {...ICON_STROKE} />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Обзор', icon: <IconOverview /> },
  { to: '/app/billing', label: 'Балансы', icon: <IconOverview /> },
  { to: '/app/ismart', label: 'Моя школа', icon: <IconPulse /> },
  { to: '/app/future', label: 'КБ (будущее)', icon: <IconPulse /> },
  { to: '/app/notifications', label: 'Почта', icon: <IconPulse /> },
  { to: '/app/ghrunners', label: 'CI-раннеры', icon: <IconPulse /> },
  { to: '/app/hosts', label: 'Хосты', icon: <IconOverview /> },
  // Was «Админы» — the page now also owns the notification channels, so the
  // label follows the content. /admins keeps working as an alias (bookmarks).
  { to: '/settings', label: 'Настройки', icon: <IconPeople /> },
];

/**
 * The app shell: a sidebar (brand, nav, health chips) + a topbar (page title,
 * theme toggle, user chip) around the routed page.
 */
export function AppLayout() {
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__logo" aria-hidden="true">
            OM
          </span>
          <span className="sidebar__title">Ops Monitor</span>
        </div>
        <nav className="sidebar__nav" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
              }
            >
              <span className="sidebar__icon">{item.icon}</span>
              <span className="sidebar__label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <ApiStatusChips />
        </div>
      </aside>

      <div className="shell__main">
        <header className="topbar">
          <div className="topbar__spacer" />
          <ThemeToggle />
          <UserChip />
        </header>
        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
